// path: src/components/Dashboard/RoleDashboard/Profile.jsx

import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../apiFetch";
import { useNavigate, useLocation } from "react-router-dom";
import "./Profile.css";
import { resolveId, rolePath, getEntitySlug } from "./roleApi";


const isDev =
  typeof process !== "undefined"
    ? process.env.NODE_ENV !== "production"
    : true;

const VALID_ROLES = ["agent", "scout", "club"];

function normalizeRole(r) {
  const role = String(r || "").toLowerCase();
  return VALID_ROLES.includes(role) ? role : "agent";
}

/**
 * Detect a sensible base path:
 * - If prop basePath is provided, normalize and use that.
 * - Else, auto-detect /(agent|scout|club)-dashboard from the current URL.
 * - Else, default to '/agent-dashboard'.
 */
function useBasePath(basePathProp) {
  const { pathname } = useLocation();

  return useMemo(() => {
    // If caller explicitly passed basePath, sanitize and use it
    if (basePathProp != null && basePathProp !== "") {
      // remove leading/trailing slashes, then re-add single leading slash
      const cleaned = `/${String(basePathProp).replace(/^\/|\/$/g, "")}`;
      // If someone literally passed "/" we allow ""
      return cleaned === "/" ? "" : cleaned;
    }

    // ✅ auto-detect the dashboard prefix from current URL
    // matches "/agent-dashboard", "/scout-dashboard", "/club-dashboard"
    const m = pathname.match(/\/(agent|scout|club)-dashboard(\/|$)/i);
    if (m) return `/${m[1].toLowerCase()}-dashboard`;

    // fallback
    return "/agent-dashboard";
  }, [basePathProp, pathname]);
}

const Profile = ({
  onProfileImageChange,
  role: roleProp, // passed in by RoleDashboard's component registry
  id,
  agentId,
  scoutId,
  clubId,
  userId,
  /**
   * Optional override. Usually you can omit this because useBasePath()
   * will auto-detect "/agent-dashboard", "/scout-dashboard", or "/club-dashboard".
   * RoleDashboard already passes basePath to be explicit.
   */
  basePath = "",
}) => {
  const navigate = useNavigate();
  const basePathResolved = useBasePath(basePath);

  const [profileImage, setProfileImage] = useState(null);
  const [profileSlug, setProfileSlug] = useState(null);

  const [loading, setLoading] = useState(false);


  // Decide which role this card is representing:
  // 1. explicit prop from RoleDashboard (best)
  // 2. localStorage userRole
  // 3. default "agent"
  const role = normalizeRole(
    roleProp ||
    (typeof window !== "undefined" &&
      (localStorage.getItem("role") || localStorage.getItem("userRole"))) ||
    "agent"
  );


  // Figure out which ID to use for fetches/nav
  const eid = resolveId({
    role,
    id,
    agentId,
    scoutId,
    clubId,
    userId,
  });

  // Unified REST endpoints
  // /api/:role/:id/profile
  // /api/:role/:id/profile/upload
  const url = rolePath({ role, id: eid }, "profile");
  const uploadUrl = rolePath({ role, id: eid }, "profile/upload");

  if (isDev) {
    console.debug("[RoleDashboard/Profile.jsx] render", {
      roleProp,
      role,
      id,
      agentId,
      scoutId,
      clubId,
      userId,
      eid,
      url,
      uploadUrl,
      basePathProp: basePath,
      basePathResolved,
    });
  }

  

  // Load profile image from backend (now with optional Authorization header)
  useEffect(() => {
    const fetchProfileImage = async () => {
      if (!eid) {
        if (isDev)
          console.warn("[RoleDashboard/Profile.jsx] skip fetch — missing eid");
        return;
      }

      setLoading(true);

      try {
        if (isDev)
          console.info("[RoleDashboard/Profile.jsx] GET profile image", {
            url,
          });

        const response = await apiFetch(url, {
          method: "GET",
        });


        const ct = response.headers.get("content-type") || "";

        if (!response.ok) {
          const text = await response.text().catch(() => "");
          throw new Error(
            `HTTP ${response.status} ${
              ct.includes("json") ? "" : "(non-JSON)"
            } ${text.slice(0, 120)}`
          );
        }

        if (!ct.includes("application/json")) {
          const text = await response.text().catch(() => "");
          throw new Error(
            `Unexpected content-type for profile image (expected JSON). ${text.slice(
              0,
              120
            )}`
          );
        }

        const data = await response.json();

        // ✅ store profileSlug for PUBLIC navigation
        setProfileSlug(data.profileSlug || null);






        const img =
          data.profileImageUrl ||
          data.avatarUrl ||
          null;

        if (img) {
          setProfileImage(img);
          onProfileImageChange?.(img);
        } else {
          setProfileImage(null);
          onProfileImageChange?.(null);
        }


        if (isDev)
          console.debug(
            "[RoleDashboard/Profile.jsx] profile image data",
            data
          );
      } catch (err) {
        console.error(
          `❌ Error loading ${role} profile image (RoleDashboard):`,
          err
        );

        // optional improvement: reset image state on error
        setProfileImage(null);
        onProfileImageChange?.(null);

      } finally {
        setLoading(false);
      }
    };

    fetchProfileImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eid, url, role]);

  // Upload profile image (NOW WITH JWT → fixes "No token provided")
  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];

    if (!file || !eid) {
      if (isDev)
        console.warn(
          "[RoleDashboard/Profile.jsx] skip upload — missing file or eid",
          { hasFile: !!file, eid }
        );
      return;
    }

    

    const formData = new FormData();
    // MUST match backend's upload.single('image')
    formData.append("image", file);



    try {
      if (isDev)
        console.info("[RoleDashboard/Profile.jsx] POST upload", {
          uploadUrl,
          fileName: file.name,
          size: file.size,
        });


      const response = await apiFetch(uploadUrl, {
        method: "POST",
        body: formData, // ✅ do NOT set Content-Type
      });


      const ct = response.headers.get("content-type") || "";

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(
          `HTTP ${response.status} ${
            ct.includes("json") ? "" : "(non-JSON)"
          } ${text.slice(0, 120)}`
        );
      }

      if (!ct.includes("application/json")) {
        const text = await response.text().catch(() => "");
        throw new Error(
          `Unexpected content-type for upload (expected JSON). ${text.slice(
            0,
            120
          )}`
        );
      }

      const data = await response.json();

      const img =
        data.profileImageUrl ||
        data.avatarUrl ||
        null;


      if (response.ok && img) {
        setProfileImage(img);
        onProfileImageChange?.(img);
      } else {
        alert(data?.error || "Upload failed");
      }

      if (isDev)
        console.debug(
          "[RoleDashboard/Profile.jsx] upload response",
          data
        );
    } catch (err) {
      console.error(
        `❌ Error uploading ${role} profile image (RoleDashboard):`,
        err
      );
    }
  };

  // Navigate to the full profile screen for this role.
  const handleViewProfile = () => {
    if (!eid) {
      console.error(
        "[RoleDashboard/Profile.jsx] Missing entity id; cannot navigate.",
        {
          role,
          id,
          agentId,
          scoutId,
          clubId,
          userId,
        }
      );
      return;
    }

    // normalize role -> slug
    const slug = getEntitySlug(normalizeRole(role)); // "agent" | "scout" | "club"

    // Build kebab-case path
    // e.g. "/scout-dashboard/scout-profile/26"
    if (!profileSlug) {
  alert("Profile is not yet public. Please complete registration.");
  return;
}



const path = `${basePathResolved}/${slug}-profile/${profileSlug}`.replace(
  /\/{2,}/g,
  "/"
);



    if (isDev) {
      console.debug(
        "[RoleDashboard/Profile.jsx] View Profile → navigate",
        {
          role,
          eid,
          basePathResolved,
          path,
          routerType: window?.location?.hash
            ? "HashRouter?"
            : "BrowserRouter?",
        }
      );
    }

    navigate(path);

    setTimeout(() => {
      if (isDev) {
        console.debug(
          "[RoleDashboard/Profile.jsx] post-navigate location",
          {
            href: window.location.href,
            pathname: window.location.pathname,
            hash: window.location.hash,
          }
        );
      }
    }, 0);
  };

  return (
    <div className="profile-section">
  <div className="profile-photo">
        {profileImage ? (
          <img
            src={profileImage}
            alt="Profile"
            className="profile-image"
            onError={() => {
              console.error(`Failed to load ${role} profile image:`, profileImage);
              setProfileImage(null);
              onProfileImageChange?.(null);
            }}
          />
        ) : (
          <span className="default-profile">
            {loading ? "..." : ""}
          </span>
        )}

    <input
      type="file"
      accept="image/*"
      onChange={handleImageChange}
      className="file-input"
      id="file-input"
    />

    <label
      htmlFor="file-input"
      className="camera-icon"
      aria-label="Upload profile photo"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        width="22"
        height="22"
        fill="yellow"
        style={{ display: "block" }}
      >
        <path d="M9 2h6l1.83 2H19a3 3 0 013 3v9a3 3 0 01-3 3H5a3 3 0 01-3-3V7a3 3 0 013-3h2L9 2zm3 6a4 4 0 100 8 4 4 0 000-8z" />
      </svg>
    </label>
  </div>

      <button
  className="view-profile-button"
  onClick={handleViewProfile}
  disabled={!profileSlug}
>

        View Profile
      </button>
    </div>
  );
};

export default Profile;
